import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { Link } from "@tanstack/react-router";
import { LogoIcon } from "./logo-icon";

const LogoMark = ({ className, alt = "AgentReacher" }: { className?: string; alt?: string }) => (
	<LogoIcon title={alt} className={cn(className, "aspect-square")} />
);

const logoVariants = cva("flex items-center", {
	variants: {
		size: {
			sm: "gap-2",
			md: "gap-2.5",
			lg: "gap-3",
			xl: "gap-4",
		},
	},
	defaultVariants: {
		size: "md",
	},
});

const logoMarkVariants = cva("shrink-0", {
	variants: {
		size: {
			sm: "h-8",
			md: "h-9",
			lg: "h-10",
			xl: "h-11",
		},
	},
	defaultVariants: {
		size: "md",
	},
});

const logoTextVariants = cva("font-semibold tracking-tight", {
	variants: {
		size: {
			sm: "text-base",
			md: "text-lg",
			lg: "text-xl",
			xl: "text-2xl",
		},
	},
	defaultVariants: {
		size: "md",
	},
});

export interface LogoProps
	extends React.HTMLAttributes<HTMLDivElement>,
		VariantProps<typeof logoVariants> {
	showText?: boolean;
	showDomain?: boolean;
	disableLink?: boolean;
	textClassName?: string;
}

const Logo = React.forwardRef<HTMLDivElement, LogoProps>(
	(
		{
			className,
			size = "md",
			showText = true,
			showDomain = false,
			disableLink = false,
			textClassName,
			...props
		},
		ref,
	) => {
		const content = (
			<>
				<LogoMark className={logoMarkVariants({ size })} />
				{showText && (
					<span
						className={cn(
							logoTextVariants({ size }),
							"text-foreground",
							textClassName,
						)}
					>
						{showDomain ? "AgentReacher.com" : "AgentReacher"}
					</span>
				)}
			</>
		);

		const wrapperClass = cn(logoVariants({ size }), className);

		if (disableLink) {
			return (
				<div ref={ref} className={wrapperClass} {...props}>
					{content}
				</div>
			);
		}

		return (
			<Link to="/dashboard" className={cn(wrapperClass, "cursor-pointer")}>
				{content}
			</Link>
		);
	},
);
Logo.displayName = "Logo";

export { Logo, logoVariants };
